import {
  configApiRef,
  IdentityApi,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { FieldValidation } from '@rjsf/utils';
import { useMemo, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';

let config: any;
let identity: IdentityApi;

type GovernanceGroupValue = { name: string; id: number };

export const GovernanceGroupExtensionSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'object',
    properties: {
      name: { type: 'string' },
      id: { type: 'number' },
    },
  },
};

export const GovernanceGroup = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<GovernanceGroupValue>) => {
  config = useApi(configApiRef);
  identity = useApi(identityApiRef);
  const [status, setStatus] = useState<'idle' | 'loading' | 'found' | 'error'>(
    'idle',
  );
  const [errorMsg, setErrorMsg] = useState('');

  const handleBlur = useCallback(
    async (e: React.FocusEvent<HTMLInputElement>) => {
      const name = e.target.value?.trim() ?? '';
      if (!name) return;

      setStatus('loading');
      setErrorMsg('');

      try {
        const { token } = await identity.getCredentials();
        const backendHost = config.getString('backend.baseUrl');
        const response = await fetch(
          `${backendHost}/api/validation/governance-group`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ name }),
          },
        );
        const result = await response.json();

        if (result.exists && result.id) {
          setStatus('found');
          onChange({ name, id: result.id });
        } else {
          setStatus('error');
          setErrorMsg(result.error || 'Governance group not found.');
          onChange({ name, id: 0 });
        }
      } catch {
        setStatus('error');
        setErrorMsg('Failed to validate governance group.');
        onChange({ name, id: 0 });
      }
    },
    [onChange],
  );

  let helperText = 'Enter the name of an existing governance group';
  if (status === 'loading') {
    helperText = 'Validating governance group...';
  } else if (status === 'found') {
    helperText = 'Governance group found';
  } else if (status === 'error') {
    helperText = errorMsg;
  }

  return (
    <div>
      <h3>Governance Group</h3>
      <Grid style={{ margin: '2px 2px 2px 2px' }}>
        <FormControl
          margin="normal"
          required={required}
          error={
            rawErrors !== undefined && rawErrors.length > 0 && !formData?.name
          }
        >
          <TextField
            label={<strong>Governance group name *</strong>}
            id="governanceGroupInput"
            value={formData?.name ?? ''}
            onChange={e => {
              setStatus('idle');
              setErrorMsg('');
              onChange({ name: e.target?.value?.trim() ?? '', id: 0 });
            }}
            onBlur={handleBlur}
            fullWidth
            style={{ width: '550px', margin: '-15px 5px -5px -5px' }}
            variant="outlined"
            helperText={helperText}
            error={status === 'error'}
          />
        </FormControl>
      </Grid>
    </div>
  );
};

export const validateGovernanceGroup = async (
  value: any,
  validation: FieldValidation,
) => {
  const groupName = value?.name?.trim?.() ?? '';
  if (!groupName) {
    validation.addError('Please provide a governance group name.');
    return;
  }

  if (!value?.id) {
    validation.addError(
      `Governance group "${groupName}" was not found. Please verify the name.`,
    );
  }
};

export const TeamMembershipExtensionSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

export const TeamMembership = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  config = useApi(configApiRef);
  identity = useApi(identityApiRef);
  const [error, setError] = useState('');

  return (
    <div>
      <h3>Team member email address</h3>
      <Grid style={{ margin: '2px 2px 2px 2px' }}>
        <FormControl
          margin="normal"
          required={required}
          error={rawErrors !== undefined && rawErrors.length > 0 && !formData}
        >
          <TextField
            label={<strong>Team member email address *</strong>}
            id="teamMembershipInput"
            defaultValue={formData}
            onChange={e => {
              const inputEmail = e.target.value?.trim();
              if (
                !/[A-Za-z0-9.]+((@principal.com)|(@cuprum.cl))/.test(inputEmail)
              ) {
                setError(
                  'Invalid Principal/Cuprum email. e.g. lastname.firstname@principal.com or email@cuprum.cl',
                );
              } else {
                setError('');
              }
              onChange(inputEmail);
            }}
            fullWidth
            style={{ width: '550px', margin: '-15px 5px -5px -5px' }}
            variant="outlined"
            helperText={error}
            error={!!error}
          />
        </FormControl>
      </Grid>
    </div>
  );
};

const validateTeamMemberEmail = async (
  value: string,
  validation: FieldValidation,
): Promise<void> => {
  const regex =
    '[A-Za-z0-9.]+((@principal.com)|(@cuprum.cl))(?:,(?=[A-Za-z0-9.]+((@principal.com)|(@cuprum.cl))))*';
  const regexp = new RegExp(regex);
  if (!regexp.test(value)) {
    validation.addError('Please provide a Principal or Cuprum email address');
  } else {
    const { token } = await identity.getCredentials();
    const backendHost = config.getString('backend.baseUrl');
    const idnResponse = await fetch(`${backendHost}/api/validation/fte`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ emails: value }),
    });

    const response = await idnResponse.json();
    if (response?.failures?.length > 0) {
      validation.addError(
        'The team member must be a full time Principal employee',
      );
    }
    if (response?.errors?.length > 0) {
      validation.addError(
        'Internal error occurred for email validation. Please try again later.',
      );
    }
  }
};

export const validateTeamMembership = async (
  value: any,
  validation: FieldValidation,
) => {
  if (typeof value === 'string') {
    if (!value.trim()) {
      validation.addError('Please provide a team member email address.');
    } else {
      await validateTeamMemberEmail(value, validation);
    }
    return;
  }
  const memList: Array<string> = value as Array<string>;
  const unique = new Set(memList.filter(m => m !== ''));
  if (unique.size < 2) {
    validation.addError('A minimum of two team members are required.');
  } else {
    for (const member of value as Array<string>) {
      if (member) {
        await validateTeamMemberEmail(member, validation);
      } else {
        validation.addError(
          'Please enter a valid email address. E.g., lastname.firstname@principal.com or email@cuprum.cl',
        );
      }
    }
  }
};

export const OrgExtensionSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

export const OrgName = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  const defaultOrgUnit = useMemo(() => {
    const org = formData ?? [];

    return org;
  }, [formData]);

  const orgFormContext = useForm({
    defaultValues: {
      orgUnit: defaultOrgUnit,
    },
  });

  const triggerOnChange = () => {
    const { orgUnit } = orgFormContext.getValues();

    onChange(`${orgUnit}`);
  };

  const onOrgChange = (e: any) => {
    orgFormContext.setValue('orgUnit', e.target?.value ?? '');
    triggerOnChange();
  };

  const OrgEnum: { [key: string]: string } = {
    CHILE: 'principalchile-emu',
    DESIGNSYSTEM: 'principaldesignsystem-emu',
    DRUPAL: 'principaldrupal-emu',
    EBS: 'principalebs-emu',
    EBSDIGITALADVICE: 'principalebsdigitaladvice-emu',
    EDA: 'principaleda-emu',
    GLOBALASSETMANAGEMENT: 'principalglobalassetmanagement-emu',
    HONGKONG: 'principalhongkong-emu',
    IS: 'principalinformationservices-emu',
    MAINFRAME: 'principalmainframe-emu',
    MEXICO: 'principalmexico-emu',
    PFG: 'principalfinancialgroup-emu',
    RIS: 'principalris-emu',
    SBX: 'principalsbx-emu',
    SEASIA: 'principalseasia-emu',
    USIS: 'principalusis-emu',
  };

  return (
    <div>
      <h3>GitHub Location</h3>
      <Typography>
        Choose the GitHub org (EMU) where this team should be built initially.
      </Typography>
      <h4>GitHub Org (EMU)</h4>
      <FormControl
        required={required}
        error={rawErrors !== undefined && rawErrors.length > 0 && !formData}
        variant="outlined"
      >
        <InputLabel id="org-unit-label" style={{ fontWeight: 700 }}>
          Organization
        </InputLabel>
        <Select
          value={defaultOrgUnit}
          id="org-unit-select"
          labelId="org-unit-label"
          labelWidth={110}
          onChange={onOrgChange}
          style={{ minWidth: '300px' }}
        >
          {Object.keys(OrgEnum).map((enumKey: any) => (
            <MenuItem key={enumKey} value={enumKey}>
              {OrgEnum[enumKey]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
